import React, { useState, useRef, useEffect } from 'react';
import { getBusinesses } from '../services/api';
import { Search, X, Store } from 'lucide-react';

const BusinessSearchSelect = ({ value, onChange, placeholder = 'Buscar negocio...' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Load selected business name if value is set externally
  useEffect(() => {
    if (value && !selected) {
      getBusinesses({ q: '', limit: 100 }).then((res) => {
        const found = res.data.find((b) => b.id === value);
        if (found) setSelected(found);
      }).catch(() => {});
    }
    if (!value) setSelected(null);
  }, [value]);

  const searchBusinesses = async (q) => {
    try {
      setLoading(true);
      const res = await getBusinesses({ q, limit: 20 });
      setResults(res.data);
    } catch (error) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchBusinesses(val);
    }, 300);
  };

  const handleSelect = (business) => {
    setSelected(business);
    setQuery('');
    setOpen(false);
    onChange(business.id, business);
  };

  const handleClear = () => {
    setSelected(null);
    setQuery('');
    setResults([]);
    onChange('', null);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setOpen(true);
    if (results.length === 0) searchBusinesses(query);
  };

  return (
    <div ref={wrapperRef} className="relative">
      {selected ? (
        <div className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg bg-white">
          <Store className="w-4 h-4 text-teal-600 flex-shrink-0" />
          <span className="text-sm font-medium text-slate-900 flex-1 truncate">{selected.name}</span>
          {selected.rfc && <span className="text-xs text-slate-400">{selected.rfc}</span>}
          <button onClick={handleClear} className="p-0.5 hover:bg-slate-100 rounded"><X className="w-4 h-4 text-slate-400" /></button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={handleFocus}
            placeholder={placeholder}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>
      )}

      {open && !selected && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-slate-500 text-center">Buscando...</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500 text-center">
              {query ? 'Sin resultados' : 'Escribe para buscar'}
            </div>
          ) : (
            results.map((b) => (
              <button
                key={b.id}
                onClick={() => handleSelect(b)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
              >
                <Store className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{b.name}</p>
                  <p className="text-xs text-slate-400">{b.rfc || 'Sin RFC'}</p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessSearchSelect;
